import type {
  RowSelectionState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";
import React, { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { DownloadIcon, type DownloadIconHandle } from "@/components/ui/download";
import { FieldFilterSelect } from "@/components/trade-filter-selects";
import { cn } from "@/lib/utils";
import { useIsBelowLg, useIsMobile } from "@/hooks/use-mobile";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { Trade, TradeFilters } from "../../../../types/trade";
import { useDisplayTimeZone } from "../../../../contexts/DisplayTimezoneContext";
import {
  formatTradeDateOnlyForDisplay,
  formatTradeDateTimeForDisplay,
} from "../../../../utils/displayDateTime";
import {
  TradeLogTableToolbar,
} from "./TradeLogActionBar";
import { TradeLogEmptyState } from "./TradeLogEmptyState";
import {
  createTradeLogTradesTableColumns,
  createTradeLogTradesTableMobileColumns,
} from "./tradeLogTradesTableColumns";
import {
  mobileValueToSorting,
  sortingToMobileValue,
  TRADE_LOG_MOBILE_SORT_OPTIONS,
} from "./tradeLogTradesTableMobileSort";
import { sortTradeLogRowsOpenFirst } from "./tradeLogTradesTableSorting";
import type { SortDirection, SortField } from "./tradeLogTradesTableTypes";

export type { SortDirection, SortField } from "./tradeLogTradesTableTypes";

interface TradesTableProps {
  trades: Array<Trade & { floatingPnl?: number }>;
  onBulkExport?: (trades: Trade[]) => void;
  onRowClick?: (trade: Trade) => void;
  filters?: TradeFilters;
  onFiltersChange?: (filters: TradeFilters) => void;
  onLogNewTrade?: () => void;
  onExport?: () => void;
  onImport?: () => void;
  onManageTemplates?: () => void;
  availableSymbols?: string[];
  emptyStateVariant?: "no-trades" | "filter-mismatch" | null;
  flushOuterCardPadding?: boolean;
  initialSortField?: SortField;
  initialSortDirection?: SortDirection;
  hideSelectionColumn?: boolean;
  /** Hides the Columns visibility dropdown above the table. */
  showColumnVisibility?: boolean;
  /** Renders the table without the outer bordered card shell. */
  hideOuterCard?: boolean;
  /** Omits the inner bordered table chrome (defaults to match `hideOuterCard`). */
  hideTableBorder?: boolean;
  /** Broker snapshot open_trade_ids — rows count as open even when DB status is wrong. */
  brokerOpenTradeIdSet?: ReadonlySet<string>;
  /** Briefly suppress row hover highlight (e.g. while the details dialog overlay exits). */
  suppressRowHover?: boolean;
  /** Pagination sits below the bordered table shell (not inside the table card wrapper). */
  paginationBelowTable?: boolean;
  /** Highlights and expands the row matching this trade id. */
  expandedRowId?: string | null;
  /** Renders journal content below the expanded row. */
  renderExpandedRow?: (trade: Trade) => React.ReactNode;
  /** Called when pagination changes so the parent can collapse an open row. */
  onExpandedRowDismiss?: () => void;
  /** Keep full columns on mobile and scroll horizontally instead of the reduced column set. */
  enableHorizontalScrollOnMobile?: boolean;
}

const TRADE_LOG_PAGE_SIZE = 50;
const TRADE_LOG_MOBILE_PAGE_SIZE = 25;

const TradesTable: React.FC<TradesTableProps> = ({
  trades,
  onBulkExport,
  onRowClick,
  filters,
  onFiltersChange,
  onLogNewTrade,
  onExport,
  onImport,
  onManageTemplates,
  availableSymbols = [],
  emptyStateVariant = null,
  flushOuterCardPadding = false,
  initialSortField = "exitDate",
  initialSortDirection = "desc",
  hideSelectionColumn = false,
  showColumnVisibility = true,
  hideOuterCard = false,
  hideTableBorder,
  brokerOpenTradeIdSet,
  suppressRowHover = false,
  paginationBelowTable = false,
  expandedRowId = null,
  renderExpandedRow,
  onExpandedRowDismiss,
  enableHorizontalScrollOnMobile = false,
}) => {
  const isMobile = useIsMobile();
  const useMobileColumns = isMobile && !enableHorizontalScrollOnMobile;
  const isCompactToolbar = useIsBelowLg();
  const pageSize = isMobile ? TRADE_LOG_MOBILE_PAGE_SIZE : TRADE_LOG_PAGE_SIZE;
  const { formatMoney } = useDisplayCurrency();
  const displayTimeZone = useDisplayTimeZone();

  const [sorting, setSorting] = useState<SortingState>([
    { id: initialSortField, desc: initialSortDirection === "desc" },
  ]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [selectAllFromHeader, setSelectAllFromHeader] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: TRADE_LOG_PAGE_SIZE,
  });

  useEffect(() => {
    setPagination((prev) =>
      prev.pageSize === pageSize ? prev : { ...prev, pageIndex: 0, pageSize }
    );
  }, [pageSize]);

  useEffect(() => {
    if (!isMobile) return;
    setSorting((prev) => {
      const current = prev[0];
      if (!current) return [{ id: "exitDate", desc: true }];
      if (current.id === "entryDate") {
        return [{ id: "exitDate", desc: current.desc }];
      }
      return prev;
    });
  }, [isMobile]);

  const pageCount =
    trades.length === 0 ? 0 : Math.ceil(trades.length / pagination.pageSize);

  const sortedTrades = useMemo(
    () => sortTradeLogRowsOpenFirst(trades, sorting, brokerOpenTradeIdSet),
    [trades, sorting, brokerOpenTradeIdSet],
  );

  useEffect(() => {
    if (pageCount > 0 && pagination.pageIndex >= pageCount) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [pagination.pageIndex, pageCount]);

  // Open trades are pinned to page 1 — reset only when filters change, not on every data tick.
  useEffect(() => {
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
  }, [filters?.status, filters?.type, filters?.profitLoss, filters?.symbols?.join(",")]);

  const selectedTradeIds = useMemo(
    () =>
      Object.entries(rowSelection)
        .filter(([, selected]) => selected)
        .map(([id]) => id),
    [rowSelection],
  );

  useEffect(() => {
    if (Object.keys(rowSelection).length === 0) {
      setSelectAllFromHeader(false);
    }
  }, [rowSelection]);

  useEffect(() => {
    setSelectAllFromHeader(false);
  }, [pagination.pageIndex]);

  const prevPageIndexRef = React.useRef(pagination.pageIndex);
  useEffect(() => {
    if (prevPageIndexRef.current === pagination.pageIndex) return;
    prevPageIndexRef.current = pagination.pageIndex;
    onExpandedRowDismiss?.();
  }, [pagination.pageIndex, onExpandedRowDismiss]);

  const handleBulkExport = () => {
    if (onBulkExport && selectedTradeIds.length > 0) {
      const idSet = new Set(selectedTradeIds);
      onBulkExport(trades.filter((trade) => idSet.has(trade.id)));
    }
  };

  const formatPercent = (value: number): string => {
    return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
  };

  const columns = useMemo(() => {
    const formatDate = (date: Date | string): string =>
      formatTradeDateTimeForDisplay(date, displayTimeZone);
    const formatDateCompact = (date: Date | string): string =>
      formatTradeDateOnlyForDisplay(date, displayTimeZone);

    return useMobileColumns
      ? createTradeLogTradesTableMobileColumns({
          formatDate: formatDateCompact,
          formatMoney,
          formatPercent,
          brokerOpenTradeIdSet,
        })
      : createTradeLogTradesTableColumns({
          hideSelectionColumn,
          formatDate,
          formatMoney,
          formatPercent,
          selectAllFromHeader,
          onSelectAllFromHeaderChange: setSelectAllFromHeader,
          brokerOpenTradeIdSet,
        });
  }, [
    useMobileColumns,
    hideSelectionColumn,
    formatMoney,
    formatPercent,
    selectAllFromHeader,
    brokerOpenTradeIdSet,
    displayTimeZone,
  ]);

  const resolvedEmptyVariant = trades.length === 0 ? emptyStateVariant ?? "no-trades" : null;
  const showEmptyState = resolvedEmptyVariant != null;
  const showActionBar =
    filters != null && onFiltersChange != null && onLogNewTrade != null;

  const exportIconRef = React.useRef<DownloadIconHandle | null>(null);

  const toolbarActions =
    !hideSelectionColumn && selectedTradeIds.length > 0 ? (
      <>
        {onBulkExport ? (
          <Button
            variant="outline-brand"
            size="sm"
            data-font="primary"
            className="shrink-0"
            onClick={handleBulkExport}
            onMouseEnter={() => exportIconRef.current?.startAnimation()}
            onMouseLeave={() => exportIconRef.current?.stopAnimation()}
          >
            <DownloadIcon
              ref={exportIconRef}
              data-icon="inline-start"
              size={16}
              className="shrink-0 text-current"
              aria-hidden
            />
            <span>Export Selected</span>
          </Button>
        ) : null}
      </>
    ) : null;

  const tableCardClass = hideOuterCard
    ? ""
    : "rounded-[6px] border border-border bg-card p-4 text-card-foreground shadow-none dark:bg-sidebar dark:text-sidebar-foreground";
  const resolvedHideTableBorder = hideTableBorder ?? hideOuterCard;

  const mobileSortSelect = (
    <div
      data-trade-log-trades-mobile-sort=""
      data-trade-log-filter-bar=""
      className="min-w-0"
    >
      <FieldFilterSelect
        value={sortingToMobileValue(sorting)}
        onValueChange={(value) => setSorting(mobileValueToSorting(value))}
        options={[...TRADE_LOG_MOBILE_SORT_OPTIONS]}
        placeholder="Sort"
      />
    </div>
  );

  const filterToolbar = showActionBar ? (
    <TradeLogTableToolbar
      filters={filters!}
      onFiltersChange={onFiltersChange!}
      availableSymbols={availableSymbols}
      variant={isCompactToolbar ? "grid" : "row"}
      trailing={isCompactToolbar ? mobileSortSelect : undefined}
    />
  ) : isCompactToolbar ? (
    <div
      data-trade-log-filter-bar=""
      data-trade-log-filter-bar-grid=""
      className="grid w-full min-w-0 grid-cols-2 gap-2 md:grid-cols-3"
    >
      {mobileSortSelect}
    </div>
  ) : null;

  const dataTable = (
    <DataTable
      columns={columns}
      data={sortedTrades}
      pageCount={pageCount}
      getRowId={(row) => row.id}
      onRowClick={onRowClick}
      activeRowId={expandedRowId ?? undefined}
      expandedRowId={expandedRowId ?? undefined}
      renderExpandedRow={renderExpandedRow}
      enableRowSelection={!hideSelectionColumn && !useMobileColumns}
      rowSelection={rowSelection}
      onRowSelectionChange={setRowSelection}
      sorting={sorting}
      onSortingChange={setSorting}
      manualSorting
      pagination={pagination}
      onPaginationChange={setPagination}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={setColumnVisibility}
      toolbarLeading={filterToolbar ?? undefined}
      toolbarActions={toolbarActions}
      showColumnVisibility={showColumnVisibility && !isCompactToolbar}
      showTableBorder={!resolvedHideTableBorder}
      tableClassName="dark:!bg-sidebar"
      columnVisibilityTradeLogTypography
      showPagination
      stackedFooter={isCompactToolbar}
      paginationAfterShell={paginationBelowTable}
    />
  );

  return (
    <div
      data-trade-log-trades-table=""
      {...(isCompactToolbar ? { "data-trade-log-trades-table-compact": "" } : {})}
      {...(useMobileColumns ? { "data-trade-log-trades-table-mobile": "" } : {})}
      {...(enableHorizontalScrollOnMobile ? { "data-trade-log-trades-table-scroll-x": "" } : {})}
      {...(suppressRowHover ? { "data-suppress-row-hover": "" } : {})}
      className="flex w-full flex-col gap-4"
    >
      {showEmptyState ? (
        <div
          data-trade-log-table-card=""
          className={cn("flex w-full flex-col gap-4", tableCardClass)}
        >
          {filterToolbar ? (
            <div
              data-trade-log-filter-row=""
              className={cn(
                "w-full min-w-0",
                isCompactToolbar ? "block" : "flex items-center gap-3",
              )}
            >
              {filterToolbar}
            </div>
          ) : null}
          <TradeLogEmptyState
            variant={resolvedEmptyVariant}
            onImportTrades={onImport ?? (() => {})}
          />
        </div>
      ) : paginationBelowTable ? (
        dataTable
      ) : (
        <div
          data-trade-log-table-card=""
          className={cn("flex w-full flex-col gap-4", tableCardClass)}
        >
          {dataTable}
        </div>
      )}
    </div>
  );
};

export default TradesTable;
